import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import DotsBackground from "../components/DotsBackground.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import AksaraCharModal from "../components/aksara/AksaraCharModal.tsx";
import GridSection from "../components/aksara/GridSection.tsx";
import SectionHeader from "../components/aksara/SectionHeader.tsx";
import {
  diacritics,
  groupedBaseChars,
  hamisaran,
  pangolat,
} from "../data/aksara/aksaraData.ts";
import { aksaraFilters, statusStyles } from "../data/ui/uiConfig.ts";
import {
  getAksaraCompletionCount,
  getAksaraStatus,
  getUserData,
} from "../utils/userStorage.ts";
import { Instagram, Linkedin } from "lucide-react";

// --- GENERATION LOGIC ---
function generateCharacters() {
  const chars: any[] = [];

  // 1. Basic characters (all)
  for (const group of groupedBaseChars) {
    const charKey = group.chars.join("/");
    const currentLesson = getAksaraCompletionCount(charKey);
    const status = getAksaraStatus(charKey);

    chars.push({
      char: charKey,
      chars: group.chars, // keep for diacritic/combos
      latin: group.latin,
      status: status,
      currentLesson: currentLesson,
      totalLesson: 3,
      type: "basic",
    });
  }

  // 2. Diacritic combinations (skip i/u)
  for (const group of groupedBaseChars.filter(
    (g) => !["i", "u"].includes(g.latin)
  )) {
    for (const d of diacritics) {
      const charKey = group.chars.map((c) => c + d.mark).join("/");
      const currentLesson = getAksaraCompletionCount(charKey);
      const status = getAksaraStatus(charKey);

      chars.push({
        char: charKey,
        chars: group.chars.map((c) => c + d.mark),
        latin: group.latin.replace(/a$/, "") + d.latin,
        status: status,
        currentLesson: currentLesson,
        totalLesson: 3,
        type: "diacritic",
      });
    }
  }

  // 3. Hamisaran (ng) for all base and all diacritic combos (skip i/u)
  for (const group of groupedBaseChars.filter(
    (g) => !["i", "u"].includes(g.latin)
  )) {
    // base + ng
    const baseNgKey = group.chars.map((c) => c + hamisaran.mark).join("/");
    const baseNgCurrentLesson = getAksaraCompletionCount(baseNgKey);
    const baseNgStatus = getAksaraStatus(baseNgKey);

    chars.push({
      char: baseNgKey,
      chars: group.chars.map((c) => c + hamisaran.mark),
      latin: group.latin.replace(/a$/, "ang"),
      status: baseNgStatus,
      currentLesson: baseNgCurrentLesson,
      totalLesson: 3,
      type: "hamisaran",
    });

    // base + diacritic + ng
    for (const d of diacritics) {
      const diacriticNgKey = group.chars
        .map((c) => c + d.mark + hamisaran.mark)
        .join("/");
      const diacriticNgCurrentLesson = getAksaraCompletionCount(diacriticNgKey);
      const diacriticNgStatus = getAksaraStatus(diacriticNgKey);

      chars.push({
        char: diacriticNgKey,
        chars: group.chars.map((c) => c + d.mark + hamisaran.mark),
        latin: group.latin.replace(/a$/, "") + d.latin + "ng",
        status: diacriticNgStatus,
        currentLesson: diacriticNgCurrentLesson,
        totalLesson: 3,
        type: "hamisaran-diacritic",
      });
    }
  }

  // 4. Pangolat (elision) for all base except a, i, u
  for (const group of groupedBaseChars.filter(
    (g) => !["a", "i", "u"].includes(g.latin)
  )) {
    const pangolatKey = group.chars.map((c) => c + pangolat.mark).join("/");
    const pangolatCurrentLesson = getAksaraCompletionCount(pangolatKey);
    const pangolatStatus = getAksaraStatus(pangolatKey);

    chars.push({
      char: pangolatKey,
      chars: group.chars.map((c) => c + pangolat.mark),
      latin: group.latin.replace(/a$/, ""),
      status: pangolatStatus,
      currentLesson: pangolatCurrentLesson,
      totalLesson: 3,
      type: "pangolat",
    });
  }

  return chars;
}

// Use imported filters
const filters = aksaraFilters;

const getBoxStyle = (status: string, darkMode: boolean) => {
  const style = statusStyles[status] || statusStyles["not-started"];
  return darkMode ? style.box.dark : style.box.light;
};

const getBarStyle = (status: string, darkMode: boolean) => {
  const style = statusStyles[status] || statusStyles["not-started"];
  return darkMode ? style.bar.dark : style.bar.light;
};

// --- GROUP CHARACTERS BY TYPE ---
function groupCharactersByType(characters: any[]) {
  return {
    basic: characters.filter((c) => c.type === "basic"),
    diacritic: characters.filter((c) => c.type === "diacritic"),
    hamisaran: characters.filter((c) => c.type === "hamisaran"),
    hamisaranDiacritic: characters.filter(
      (c) => c.type === "hamisaran-diacritic"
    ),
    pangolat: characters.filter((c) => c.type === "pangolat"),
  };
}

function filterDiacriticByName(characters, name) {
  return characters.filter(
    (c) =>
      c.type === "diacritic" &&
      diacritics.find((d) => d.name === name && c.char.includes(d.mark))
  );
}
function filterHamisaranBase(characters) {
  return characters.filter((c) => c.type === "hamisaran");
}
function filterHamisaranDiacritic(characters, name) {
  return characters.filter(
    (c) =>
      c.type === "hamisaran-diacritic" &&
      diacritics.find((d) => d.name === name && c.char.includes(d.mark))
  );
}
function filterPangolat(characters) {
  return characters.filter((c) => c.type === "pangolat");
}

function getEmptyText(filter: string) {
  switch (filter) {
    case "mastered":
      return "Belum ada aksara yang selesai dipelajari";
    case "active":
      return "Belum ada aksara yang sedang dipelajari";
    case "not-started":
      return "Semua aksara telah dipelajari";
    default:
      return "";
  }
}

const AksaraPage = () => {
  const [currentSection, setCurrentSection] = useState("Aksara");
  const [filter, setFilter] = useState("all");
  const [characters, setCharacters] = useState(() => generateCharacters());
  const [modalChar, setModalChar] = useState<null | (typeof characters)[0]>(
    null
  );
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });
  const navigate = useNavigate();

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });

  // console.log("Current Device (Aksara Page):");
  // console.log("Mobile:", isMobile);
  // console.log("Tablet:", isTablet);
  // console.log("Desktop:", isDesktop);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const filteredChars =
    filter === "all"
      ? characters
      : characters.filter((c) => c.status === filter);

  // Grouped characters for UI sections
  const grouped = groupCharactersByType(filteredChars);

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && !isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        <div
          className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
          style={
            isMobile
              ? {
                  paddingTop: 64,
                  paddingBottom: 64,
                  paddingLeft: 16,
                  paddingRight: 16,
                }
              : {}
          }
        >
          <main className="px-0 md:px-8">
            {/* Header */}
            <div
              className={`w-full max-w-2xl mx-auto text-center mb-8 mt-10 px-4 md:px-8 ${
                isMobile ? "mt-4 mb-6" : isTablet ? "mt-8 mb-8" : "mt-10 mb-8"
              }`}
            >
              <h1
                className={`font-extrabold mb-2 ${
                  isMobile ? "text-2xl" : isTablet ? "text-3xl" : "text-4xl"
                } ${darkMode ? "text-gray-100" : "text-gray-800"}`}
                style={{ fontFamily: "Feather Bold" }}
              >
                Mari Belajar Aksara Batak Toba!
              </h1>
              <p
                className={`mb-6 ${
                  isMobile ? "text-base" : isTablet ? "text-lg" : "text-lg"
                } ${darkMode ? "text-gray-200" : "text-gray-800"}`}
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                Mengenal Sistem Tulisan Batak Toba
              </p>
              {/* Filter */}
              <div
                className={`flex justify-center gap-2 mb-4 ${
                  isMobile ? "gap-1" : isTablet ? "gap-2" : "gap-2"
                }`}
              >
                {filters.map((f) => (
                  <button
                    key={f.value}
                    className={`rounded-full duration-200 transform hover:-translate-y-0.5 transition-all ${
                      isMobile
                        ? "px-2 py-1 text-xs"
                        : isTablet
                        ? "px-3 py-1 text-sm"
                        : "px-4 py-1 text-sm"
                    } ${
                      filter === f.value
                        ? "bg-blue-500 text-white"
                        : darkMode
                        ? "bg-gray-700 text-blue-200 hover:bg-gray-600"
                        : "bg-gray-200 text-blue-700 hover:bg-blue-100"
                    }`}
                    onClick={() => setFilter(f.value)}
                    style={{ fontFamily: "Feather Bold" }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* --- BASIC CHARACTERS SECTION --- */}
            <div className="max-w-3xl mx-auto mb-10">
              <SectionHeader
                title="Aksara Dasar"
                subtitle="(Ina Ni Surat)"
                darkMode={darkMode}
              />
              <GridSection
                title=""
                subtitle=""
                chars={grouped.basic}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                darkMode={darkMode}
              />
            </div>

            {/* --- DIACRITICS CHARACTERS SECTION --- */}
            <div className="max-w-3xl mx-auto mb-10">
              <SectionHeader
                title="Aksara Diakritik"
                subtitle="(Anak Ni Surat)"
                darkMode={darkMode}
              />
              <GridSection
                title="Uluwa (-i)"
                subtitle=""
                chars={filterDiacriticByName(grouped.diacritic, "uluwa")}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Boruta (-u)"
                subtitle=""
                chars={filterDiacriticByName(grouped.diacritic, "boruta")}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Hatadingan (-é)"
                subtitle=""
                chars={filterDiacriticByName(grouped.diacritic, "hatadingan")}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Siala (-o)"
                subtitle=""
                chars={filterDiacriticByName(grouped.diacritic, "siala")}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
            </div>

            {/* --- HAMISARAN CHARACTERS SECTION --- */}
            <div className="max-w-3xl mx-auto mb-10">
              <SectionHeader
                title="Hamisaran (-ng)"
                subtitle="(Aksara Dasar + Diakritik + -ng)"
                darkMode={darkMode}
              />
              <GridSection
                title="Aksara Dasar + Hamisaran (-a + -ng)"
                subtitle=""
                chars={filterHamisaranBase(grouped.hamisaran)}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Uluwa + Hamisaran (-i + -ng)"
                subtitle=""
                chars={filterHamisaranDiacritic(
                  grouped.hamisaranDiacritic,
                  "uluwa"
                )}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Boruta + Hamisaran (-u + -ng)"
                subtitle=""
                chars={filterHamisaranDiacritic(
                  grouped.hamisaranDiacritic,
                  "boruta"
                )}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Hatadingan + Hamisaran (-é + -ng)"
                subtitle=""
                chars={filterHamisaranDiacritic(
                  grouped.hamisaranDiacritic,
                  "hatadingan"
                )}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
              <GridSection
                title="Siala + Hamisaran (-o + -ng)"
                subtitle=""
                chars={filterHamisaranDiacritic(
                  grouped.hamisaranDiacritic,
                  "siala"
                )}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
                dividerClassName="mb-4 md:mx-6"
              />
            </div>

            {/* --- PANGOLAT CHARACTERS SECTION --- */}
            <div className="max-w-3xl mx-auto mb-10">
              <SectionHeader
                title="Pangolat (Pemati)"
                subtitle="(Aksara Dasar + Pangolat)"
                darkMode={darkMode}
              />
              <GridSection
                title=""
                subtitle=""
                chars={filterPangolat(grouped.pangolat)}
                emptyText={getEmptyText(filter)}
                getBoxStyle={(status) => getBoxStyle(status, darkMode)}
                getBarStyle={(status) => getBarStyle(status, darkMode)}
                onCharClick={(c, idx) => {
                  setModalChar(c);
                }}
                emptyAtStart={filter === "all" ? 1 : 0}
                emptyAtEnd={filter === "all" ? 2 : 0}
                darkMode={darkMode}
              />
            </div>
            {isMobile && (
              <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
                <div
                  className="text-gray-500 text-sm font-semibold"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  &copy; 2025 Galaxy Developer
                </div>
                <div className="flex gap-4 mt-1">
                  <a
                    href="https://www.instagram.com/galaxy.developer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/galaxydeveloper"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </footer>
            )}
          </main>
        </div>
        {(isDesktop || isTablet) && (
          <div
            style={{
              position: "sticky",
              top: 0,
              right: 0,
              height: "100vh",
              zIndex: 30,
              width: 320,
              background: "transparent",
              pointerEvents: "auto",
              overflowY: "auto",
            }}
          >
            <RightSidebar darkMode={darkMode} />
          </div>
        )}
        {/* BottomNav: fixed for mobile only */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <BottomNav
              currentSection={currentSection}
              setCurrentSection={setCurrentSection}
              darkMode={darkMode}
            />
          </div>
        )}

        {/* Modal for character info */}
        <AksaraCharModal
          modalChar={modalChar}
          onClose={() => setModalChar(null)}
          onBelajar={() =>
            navigate("/aksara/learn", { state: { batakChar: modalChar } })
          }
          getBarStyle={(status) => getBarStyle(status, darkMode)}
          darkMode={darkMode}
        />
      </div>
    </div>
  );
};

export default AksaraPage;
